/**
 * The sign-in an application was sent with: the address shown plainly, the
 * password hidden behind an eye. The first look asks for the person's own
 * JobSlide password (the page may be on a shared screen); after that the eye
 * just hides and shows it until the page is left.
 *
 * Shown on every application that has an account — an employer-site account
 * the run made or signed in to, or the person's own Naukri login — whether or
 * not the form was captured.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { applicationApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { PORTAL_LABEL } from '@/features/mail/mailMeta'
import type { ApplicationDetail } from '@/types/api'

const SOURCE_NOTE: Record<string, string | null> = {
  application: null,
  naukri: 'Your Naukri login',
  settings: 'Your current application password (this application predates the per-application record)',
}

export function AccountUsedCard({ application }: { application: ApplicationDetail }) {
  const [asking, setAsking] = useState(false)
  const [password, setPassword] = useState('')
  const [revealed, setRevealed] = useState<{ password: string; source: string } | null>(null)
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)
  const reveal = useMutation({
    mutationFn: () => applicationApi.revealAccountPassword(application.id, password),
    onSuccess: (data) => {
      setRevealed({ password: data.password, source: data.source })
      setShown(true)
      setPassword('')
      setAsking(false)
    },
  })
  const email = application.portalAccountEmail ?? null
  if (!email) return null
  const portal = PORTAL_LABEL[application.portalType ?? ''] ?? 'the employer site'
  const error = reveal.error instanceof ApiError ? reveal.error.message : reveal.error ? 'Could not show the password' : null
  const note = revealed ? SOURCE_NOTE[revealed.source] : null

  const toggle = () => {
    if (revealed) setShown((value) => !value)
    else setAsking(true)
  }
  const copy = async () => {
    if (!revealed) return
    try {
      await navigator.clipboard.writeText(revealed.password)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked: the password is on screen */
    }
  }

  return (
    <section className="card overflow-hidden">
      <header className="border-b border-line px-4 py-3 sm:px-5">
        <h2 className="text-[14.5px] font-semibold text-ink">Account used on {portal}</h2>
        <p className="mt-0.5 text-[12.5px] text-ink-muted">Sign in there yourself with these to check or withdraw the application.</p>
      </header>
      <dl className="grid gap-x-6 gap-y-2 px-4 py-3 text-[13px] sm:grid-cols-[auto_1fr] sm:px-5">
        <dt className="text-ink-muted">Username</dt>
        <dd className="min-w-0 break-all font-medium text-ink">{email}</dd>
        <dt className="text-ink-muted">Password</dt>
        <dd className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-field bg-surface-muted px-2 py-0.5 text-[13px] text-ink" aria-live="polite">
              {revealed && shown ? revealed.password : '••••••••••'}
            </code>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-field text-ink-muted hover:bg-surface-muted hover:text-ink"
              aria-label={revealed && shown ? 'Hide password' : 'Show password'}
              aria-pressed={Boolean(revealed && shown)}
              onClick={toggle}
            >
              {revealed && shown ? <EyeOff /> : <Eye />}
            </button>
            {revealed && shown && (
              <button type="button" className="text-[12.5px] font-medium text-brand hover:underline" onClick={copy}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
          {asking && !revealed && (
            <form
              className="mt-2 flex flex-wrap items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault()
                if (password) reveal.mutate()
              }}
            >
              <input
                type="password"
                autoComplete="current-password"
                autoFocus
                className="field-box h-8 w-56 px-2 text-[13px]"
                placeholder="Your JobSlide password to confirm"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Button type="submit" size="xs" loading={reveal.isPending} disabled={!password}>
                Show
              </Button>
              <Button type="button" size="xs" variant="ghost" onClick={() => setAsking(false)}>
                Cancel
              </Button>
              {error && <span className="text-[12.5px] text-danger">{error}</span>}
            </form>
          )}
          {note && shown && <p className="mt-1 text-[12px] text-ink-faint">{note}</p>}
        </dd>
      </dl>
    </section>
  )
}

function Eye() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 10s2.7-5 7.5-5 7.5 5 7.5 5-2.7 5-7.5 5-7.5-5-7.5-5Z" />
      <circle cx="10" cy="10" r="2.2" />
    </svg>
  )
}

function EyeOff() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3l14 14M8.2 5.3A7.6 7.6 0 0 1 10 5c4.8 0 7.5 5 7.5 5a12.6 12.6 0 0 1-2.2 2.8M12 14.6a7 7 0 0 1-2 .4c-4.8 0-7.5-5-7.5-5a12.4 12.4 0 0 1 3.1-3.4" />
      <path d="M8.4 8.5a2.2 2.2 0 0 0 3.1 3.1" />
    </svg>
  )
}
